// Ranged archetype (MOB-2): hostiles that keep a standoff band and fire a
// projectile every `fireInterval` (skeleton → arrow, MOB-5). PURE (ENG-1).
import { PROJECTILE } from '../../config';
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext } from '../entity';
import { wander, moveToward, moveAway, tickSpeed, difficultyScale } from './shared';
import { fireArrowAt } from '../projectiles/arrow';
import { throwSplashPotionAt } from '../projectiles/potion';

export function ranged(e: Entity, def: MobDef, ctx: MobContext): void {
  const { frame, sys } = ctx;
  const scale = difficultyScale(frame.difficulty);
  if (scale === 0) { wander(e, ctx, def); return; }

  const near = sys.nearestPlayer(frame, e.x, e.y, e.z, def.followRange);
  if (!near) { e.targetIdx = -1; if (e.data.fireCd) e.data.fireCd = 0; wander(e, ctx, def); return; }

  e.targetIdx = near.idx;
  const pl = frame.players[near.idx]!;
  const dist = near.dist;

  // Maintain the standoff band: close in when far, back off when too near.
  if (dist > def.standoffMax) moveToward(e, ctx, pl.x, pl.z, tickSpeed(def, 1));
  else if (dist < def.standoffMin) moveAway(e, ctx, pl.x, pl.z, tickSpeed(def, 1));
  else e.yaw = Math.atan2(-(pl.x - e.x), -(pl.z - e.z)); // hold and aim

  // Fire on cadence while roughly in band.
  e.data.fireCd = (e.data.fireCd ?? 0) - 1;
  if (e.data.fireCd <= 0 && dist <= def.followRange && dist >= def.standoffMin * 0.5) {
    if (def.projectile === 'arrow') {
      const dmg = Math.max(1, Math.round(PROJECTILE.ARROW_SPEED * PROJECTILE.ARROW_DAMAGE_PER_SPEED * scale));
      // A stray tips its arrows with its onHitEffect (slowness).
      const tip = def.onHitEffect
        ? { id: def.onHitEffect.effect, amplifier: def.onHitEffect.amplifier, ticks: Math.round(def.onHitEffect.seconds * 20) }
        : undefined;
      fireArrowAt(sys, e, pl.x, pl.y + pl.eyeY * 0.5, pl.z, dmg, tip);
    } else if (def.projectile === 'splash_potion') {
      // A witch lobs a harmful splash potion; onHitEffect names the potion (poison).
      const eff = def.onHitEffect
        ? { id: def.onHitEffect.effect, amplifier: def.onHitEffect.amplifier, ticks: Math.round(def.onHitEffect.seconds * 20) }
        : { id: 'poison', amplifier: 0, ticks: 900 };
      throwSplashPotionAt(sys, e, pl.x, pl.y + pl.eyeY * 0.5, pl.z, eff);
    }
    e.data.fireCd = def.fireInterval;
  }
}
